import { twMerge } from "tailwind-merge";
import clsx from "clsx";
import { Gender, Step } from "../types/interfaces";
import posthog from "./posthog";
import { MediaPermissionsErrorType, MediaPermissionsError } from "mic-check";
import { requestVideoPermissions } from "mic-check/lib/requestMediaPermissions.js";
import { v4 as uuidv4 } from "uuid";
import { BodyScanObjMetaData } from "@swan-admin/swan-ai-measurements/dist/constants";

export const resolveSteps = (steps: Step[]): Step[] => {
	if (!steps.length) {
		throw new Error("❌ Onboarding requires at least one step.");
	}

	// Required types
	const required = ["email", "name", "gender", "height"];
	const missing = required.filter((r) => !steps.some((s) => s.type === r));
	if (missing.length > 0) {
		throw new Error(`❌ Missing required steps: ${missing.join(", ")}`);
	}

	// Filter by visibility (default: true)
	const visible = steps.filter((s) => s.isVisible !== false);

	// Separate ordered + unordered
	const ordered = visible.filter((s) => typeof s.order === "number");
	const unordered = visible.filter((s) => typeof s.order !== "number");

	// Sort ONLY the ordered ones (by order asc)
	const sortedOrdered = [...ordered].sort((a, b) => a.order! - b.order!);

	// 🔥 Keep unordered steps in EXACT same sequence as passed in original `steps`
	const sortedUnordered = steps.filter((s) => s.isVisible !== false && typeof s.order !== "number");

	// Final resolve:
	return [...sortedOrdered, ...sortedUnordered];
};

export const isValidEmail = (email: string) => {
	const checkEmailValidation = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return checkEmailValidation.test(email);
};
export function cn(...inputs: string[]) {
	return twMerge(clsx(inputs));
}

export function convertToCentimeters(feet: number, inches: number) {
	const feetToCentimeters = (feet && +feet * 30.48) ?? 0;
	const inchesToCentimeters = (inches && +inches * 2.54) ?? 0;
	const totalCentimeters = feetToCentimeters + inchesToCentimeters;
	return totalCentimeters;
}
export const handleErrorMessage = (error: any) =>
	error?.response?.data?.error ?? error?.response?.data?.message ?? error?.response?.data?.details?.[0]?.message ?? error?.message ?? error?.error ?? "Something went wrong";

export const getBrowserName = () => {
	const browserInfo = navigator.userAgent;
	let browser;
	if (browserInfo.includes("Opera") || browserInfo.includes("Opr")) {
		browser = "Opera";
	} else if (browserInfo.includes("Edg")) {
		browser = "Edge";
	} else if (browserInfo.includes("Chrome") || navigator.vendor.toLowerCase().includes("google")) {
		browser = "Chrome";
	} else if (browserInfo.includes("Safari")) {
		browser = "Safari";
	} else if (browserInfo.includes("Firefox")) {
		browser = "Firefox";
	} else {
		browser = "Browser";
	}
	return browser;
};

export const handleWebSocketCapture = ({
	eventName,
	scanID,
	connection,
	type,
	email,
	faceScanID,
}: {
	eventName: string;
	email: string;
	faceScanID?: string;
	scanID?: string;
	type: string;
	connection: string;
}) => {
	const timestamp = new Date().toISOString();
	const payload: Record<string, string> = {
		connection,
		...(scanID ? { scanID } : {}),
		timestamp,
		type,
		...(faceScanID ? { faceScanID } : {}),
	};
	if (email) {
		payload.email = email;
	}
	posthog.capture(eventName, payload);
};

export const handleScanTimeCapture = ({
	eventName,
	scanID,
	status,
	email,
	completionTime = 0,
	message = "",
	data,
	faceScanId,
	startTime,
	uploadDuration,
}: {
	eventName: string;
	scanID?: string;
	status?: string;
	completionTime?: number;
	email: string;
	message?: string;
	data?: string;
	faceScanId?: string;
	startTime?: number;
	uploadDuration?: number;
}) => {
	const timestamp = new Date().toISOString();

	const payload = Object.fromEntries(
		Object.entries({
			timestamp,
			status,
			scanID,
			faceScanId,
			email,
			completionTime,
			message,
			data,
			startTime,
			uploadDuration,
		}).filter(([, value]) => value !== undefined && value !== "" && value !== 0),
	);

	posthog.capture(eventName, payload);
};

export const checkCameraPermission = async () => {
	try {
		await requestVideoPermissions();

		return { disabled: false, message: "" };
	} catch (err) {
		if (typeof err === "object" && err !== null && "type" in err) {
			const { type } = err as MediaPermissionsError;

			if (type === MediaPermissionsErrorType.Generic || type === MediaPermissionsErrorType.CouldNotStartVideoSource) {
				return { disabled: true, message: "Could not start video source" };
			}

			if (type === MediaPermissionsErrorType.SystemPermissionDenied || type === MediaPermissionsErrorType.UserPermissionDenied) {
				return { disabled: true, message: "Permission denied" };
			}
		}

		return { disabled: true, message: "An unknown error occurred" };
	}
};

export const generateUuid = () => {
	return uuidv4();
};

export const getCurrentTimeInSeconds = () => Math.round(Date.now() / 1000);

export const isIOSDevice = () => typeof navigator !== "undefined" && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

export const getPreferredMediaRecorderTypes = (isIOS = isIOSDevice()) => (isIOS ? ["video/mp4; codecs=h264,aac", "video/mp4"] : ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"]);

export const getSupportedMediaRecorderTypes = (preferredTypes: string[], fallbackTypes: string[] = []) => {
	if (typeof MediaRecorder === "undefined") return [];
	const supportedPreferred = preferredTypes.filter((type) => MediaRecorder.isTypeSupported(type));
	if (supportedPreferred.length > 0) return supportedPreferred;
	return fallbackTypes.filter((type) => MediaRecorder.isTypeSupported(type));
};

export const getRecordingMimeType = (supportedTypes: string[], isIOS = isIOSDevice()) => supportedTypes[0] || (isIOS ? "video/mp4" : "video/webm");

export const getRecordingExtension = (mimeType: string) => (mimeType.includes("mp4") ? "mp4" : "webm");

export const createObjectMetadataArray = ({
	clothes_fit,
	gender,
	email,
	focal_length,
	scan_type,
	customer_store_url,
	height,
	callback_url,
	source_tag
}: Partial<Omit<BodyScanObjMetaData, "callback_url">> & { callback_url?: string | null }): Partial<BodyScanObjMetaData>[] => {
	const optional: Array<[keyof BodyScanObjMetaData, any]> = [
		["email", email],
		["focal_length", focal_length != null ? `${focal_length}` : undefined],
		["height", height != null ? `${height}` : undefined],
		["customer_store_url", customer_store_url],
		["gender", gender?.toLowerCase()],
		["scan_type", scan_type],
		["callback_url", callback_url],
		["source_tag", source_tag],
	];

	return [{ clothes_fit }, ...optional.filter(([, v]) => v != null).map(([k, v]) => ({ [k]: v }) as Partial<BodyScanObjMetaData>)];
};

export const rescanSupportCaptureEvent = ({
	eventName,
	scanID,
	status = false,
	gender,
	email,
	height,
}: {
	eventName: string;
	scanID: string;
	status: boolean;
	gender: Gender;
	email: string;
	height: number;
}) => {
	const timestamp = new Date().toISOString();
	const prop = { scanID, timestamp, email, height, gender, status };
	posthog.capture(eventName, prop);
};

export const getVideoFPS = (file: File): Promise<number | null> => {
	return new Promise((resolve) => {
		const video = document.createElement("video") as HTMLVideoElement & {
			webkitDecodedFrameCount?: number;
			requestVideoFrameCallback?: (cb: () => void) => number;
			cancelVideoFrameCallback?: (id: number) => void;
		};

		video.preload = "auto";
		video.muted = true;
		video.playsInline = true;

		const objectUrl = URL.createObjectURL(file);
		video.src = objectUrl;

		let resolved = false;
		let rafHandle: number | null = null;

		// Keep reference for cleanup
		let timeUpdateHandler: (() => void) | null = null;

		// ✅ Cleanup always safe
		const cleanup = () => {
			video.pause();

			if (timeUpdateHandler) {
				video.removeEventListener("timeupdate", timeUpdateHandler);
			}

			if (rafHandle !== null && video.cancelVideoFrameCallback) {
				video.cancelVideoFrameCallback(rafHandle);
			}

			URL.revokeObjectURL(objectUrl);
		};

		// ✅ Resolve only once
		const finish = (fps: number | null) => {
			if (resolved) return;
			resolved = true;
			cleanup();
			resolve(fps);
		};

		// ✅ Hard timeout safety
		const timeout = setTimeout(() => {
			finish(null);
		}, 2000);

		// ----------------------------
		// Method 1: requestVideoFrameCallback (Best)
		// ----------------------------
		const tryFrameCallbackMethod = () => {
			if (!video.requestVideoFrameCallback) {
				tryTimeUpdateMethod();
				return;
			}

			let frameCount = 0;
			const start = performance.now();

			const countFrames = () => {
				frameCount++;
				const elapsed = (performance.now() - start) / 1000;

				if (elapsed >= 1) {
					clearTimeout(timeout);
					const fps = frameCount / elapsed;
					finish(fps > 0 ? fps : null);
					return;
				}

				rafHandle = video.requestVideoFrameCallback!(countFrames);
			};

			video
				.play()
				.then(() => {
					rafHandle = video.requestVideoFrameCallback!(countFrames);
				})
				.catch(() => {
					tryTimeUpdateMethod();
				});
		};

		// ----------------------------
		// Method 2: webkitDecodedFrameCount (Safari)
		// ----------------------------
		const tryWebkitDecodedMethod = () => {
			if (video.webkitDecodedFrameCount === undefined) {
				tryFrameCallbackMethod();
				return;
			}

			video
				.play()
				.then(() => {
					setTimeout(() => {
						if (!video.duration) {
							tryFrameCallbackMethod();
							return;
						}

						const frames = video.webkitDecodedFrameCount || 0;
						if (frames > 0) {
							clearTimeout(timeout);
							finish(frames / video.duration);
						} else {
							tryFrameCallbackMethod();
						}
					}, 500);
				})
				.catch(() => {
					tryFrameCallbackMethod();
				});
		};

		// ----------------------------
		// Method 3: timeupdate fallback (Worst but universal)
		// ----------------------------
		const tryTimeUpdateMethod = () => {
			let ticks = 0;
			const start = performance.now();

			const onTimeUpdate = () => {
				ticks++;
				const elapsed = (performance.now() - start) / 1000;

				if (elapsed >= 1) {
					clearTimeout(timeout);
					const fps = ticks / elapsed;
					finish(fps > 0 ? fps : null);
				}
			};

			timeUpdateHandler = onTimeUpdate;
			video.addEventListener("timeupdate", onTimeUpdate);

			video.play().catch(() => finish(null));
		};

		// ----------------------------
		// Start after metadata loads
		// ----------------------------
		video.onloadedmetadata = () => {
			if (!video.duration || video.duration <= 0) {
				finish(null);
				return;
			}

			// Safari method first
			tryWebkitDecodedMethod();
		};

		video.onerror = () => finish(null);
	});
};
